/**
 * Logs-Viewer-Komponente für Admin-Dashboard
 * 
 * @module components/admin/LogsViewer
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, AlertCircle, RefreshCw, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface LogsResponse {
  logs: string[];
  count: number;
}

export function LogsViewer() {
  const [limit, setLimit] = useState(100);
  const [level, setLevel] = useState<string>("");
  const [showErrors, setShowErrors] = useState(false);
  const [requestId, setRequestId] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [restaurantId, setRestaurantId] = useState<string>("");
  const [route, setRoute] = useState<string>("");
  const [timeRange, setTimeRange] = useState<string>("24h");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Berechne Time Range
  const getTimeRangeParams = () => {
    const now = Date.now();
    let startTime: number;
    switch (timeRange) {
      case "1h":
        startTime = now - 60 * 60 * 1000;
        break;
      case "6h":
        startTime = now - 6 * 60 * 60 * 1000;
        break;
      case "24h":
        startTime = now - 24 * 60 * 60 * 1000;
        break;
      case "7d":
        startTime = now - 7 * 24 * 60 * 60 * 1000;
        break;
      default:
        startTime = now - 24 * 60 * 60 * 1000;
    }
    return { startTime, endTime: now };
  };

  const { data: logsData, isLoading, error, refetch } = useQuery<LogsResponse>({
    queryKey: ["/api/admin/logs", limit, level, showErrors, requestId, userId, restaurantId, route, timeRange],
    queryFn: async () => {
      const { startTime, endTime } = getTimeRangeParams();
      const params = new URLSearchParams({
        limit: limit.toString(),
        startTime: startTime.toString(),
        endTime: endTime.toString(),
      });
      
      if (level) params.append("level", level);
      if (requestId) params.append("requestId", requestId);
      if (userId) params.append("userId", userId);
      if (restaurantId) params.append("restaurantId", restaurantId);
      if (route) params.append("route", route);

      // "Nur Fehler" setzt Level-Filter auf "error" statt separaten Endpoint zu nutzen
      // Damit bleiben alle anderen Filter (Zeitraum, requestId etc.) aktiv
      if (showErrors && !level) {
        params.append("level", "error");
      }

      const endpoint = `/api/admin/logs?${params.toString()}`;
      
      const res = await apiRequest("GET", endpoint);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Fehler beim Laden der Logs: ${res.status} ${errorText}`);
      }
      return await res.json();
    },
    refetchInterval: 10000, // Alle 10 Sekunden aktualisieren
    refetchIntervalInBackground: false, // Nicht pollen wenn Tab im Hintergrund
    retry: 3,
    retryDelay: 2000,
    staleTime: 5000,
    gcTime: 300000,
  });

  const clearFilters = () => {
    setLevel("");
    setRequestId("");
    setUserId("");
    setRestaurantId("");
    setRoute("");
    setTimeRange("24h");
  };

  const parseLogEntry = (logLine: string) => {
    try {
      const parsed = JSON.parse(logLine);
      return {
        level: parsed.level || "info",
        time: parsed.time || new Date().toISOString(),
        msg: parsed.msg || logLine,
        ...parsed,
      };
    } catch {
      return {
        level: "info",
        time: new Date().toISOString(),
        msg: logLine,
      };
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "error":
        return "text-red-600 bg-red-50";
      case "warn":
        return "text-yellow-600 bg-yellow-50";
      case "info":
        return "text-blue-600 bg-blue-50";
      case "debug":
        return "text-gray-600 bg-gray-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="mt-2 text-gray-500">Lade Logs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 mx-auto text-red-600" />
        <p className="mt-2 text-red-600">Fehler beim Laden der Logs</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter und Steuerung */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="space-y-4">
          {/* Basis-Filter */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <label className="text-sm font-medium text-gray-700">Filter:</label>
            </div>

            {!showErrors && (
              <div className="flex items-center gap-2">
                <Label htmlFor="level" className="text-sm text-gray-700 whitespace-nowrap">Level:</Label>
                <select
                  id="level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="border border-gray-300 rounded-md p-2 text-sm focus:ring-primary focus:border-primary"
                >
                  <option value="">Alle</option>
                  <option value="error">Error</option>
                  <option value="warn">Warning</option>
                  <option value="info">Info</option>
                  <option value="debug">Debug</option>
                </select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Label htmlFor="timeRange" className="text-sm text-gray-700 whitespace-nowrap">Zeitraum:</Label>
              <select
                id="timeRange"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-md p-2 text-sm focus:ring-primary focus:border-primary"
              >
                <option value="1h">Letzte Stunde</option>
                <option value="6h">Letzte 6 Stunden</option>
                <option value="24h">Letzte 24 Stunden</option>
                <option value="7d">Letzte 7 Tage</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="limit" className="text-sm text-gray-700 whitespace-nowrap">Anzahl:</Label>
              <select
                id="limit"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border border-gray-300 rounded-md p-2 text-sm focus:ring-primary focus:border-primary"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showErrors"
                checked={showErrors}
                onChange={(e) => setShowErrors(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="showErrors" className="text-sm text-gray-700">
                Nur Fehler
              </label>
            </div>

            <Button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              variant="outline"
              size="sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              Erweiterte Filter
            </Button>

            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="ml-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Aktualisieren
            </Button>
          </div>

          {/* Erweiterte Filter */}
          {showAdvancedFilters && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-gray-700">Erweiterte Filter:</Label>
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="sm"
                  className="h-7"
                >
                  <X className="h-3 w-3 mr-1" />
                  Alle zurücksetzen
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label htmlFor="requestId" className="text-xs text-gray-600">Request-ID:</Label>
                  <Input
                    id="requestId"
                    value={requestId}
                    onChange={(e) => setRequestId(e.target.value)}
                    placeholder="z.B. abc-123..."
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="userId" className="text-xs text-gray-600">User-ID:</Label>
                  <Input
                    id="userId"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="z.B. 42"
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="restaurantId" className="text-xs text-gray-600">Restaurant-ID:</Label>
                  <Input
                    id="restaurantId"
                    value={restaurantId}
                    onChange={(e) => setRestaurantId(e.target.value)}
                    placeholder="z.B. 123"
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="route" className="text-xs text-gray-600">Route/Path:</Label>
                  <Input
                    id="route"
                    value={route}
                    onChange={(e) => setRoute(e.target.value)}
                    placeholder="z.B. /api/restaurants"
                    className="mt-1 text-sm"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logs-Anzeige */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="mb-4">
          <p className="text-sm text-gray-500">
            {logsData?.count || 0} Log-Einträge
            {showErrors && " (nur Fehler)"}
          </p>
        </div>

        <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto font-mono text-sm">
          {logsData?.logs && logsData.logs.length > 0 ? (
            <div className="space-y-1">
              {logsData.logs.map((logLine, index) => {
                const entry = parseLogEntry(logLine);
                return (
                  <div
                    key={index}
                    className={`p-2 rounded ${getLevelColor(entry.level)}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-xs">
                        {new Date(entry.time).toLocaleTimeString('de-DE')}
                      </span>
                      <span className="font-semibold text-xs uppercase">
                        [{entry.level}]
                      </span>
                      <span className="flex-1">{entry.msg}</span>
                    </div>
                    {entry.method && entry.path && (
                      <div className="text-xs mt-1 opacity-75">
                        {entry.method} {entry.path}
                        {entry.statusCode && ` ${entry.statusCode}`}
                        {entry.duration && ` (${entry.duration}ms)`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500">Keine Logs gefunden</p>
          )}
        </div>
      </div>
    </div>
  );
}

